import { inject, ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, input, OnChanges, OnInit, output, signal, SimpleChanges } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';

import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { InputMask } from 'primeng/inputmask';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { Tooltip } from 'primeng/tooltip';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { AreaAtuacaoPorEspecialidadeResponse, EspecialidadeListaResponse, EspecialidadeRequest } from '@/core/api/sicom-api/registro/medico/especialidade/especialidade.model';
import { EspecialidadeService } from '@/core/api/sicom-api/registro/medico/especialidade/especialidade.service';
import { EspecialidadeTiposDocumentoService } from './especialidade.service';
import { CardContent } from '@/shared/components/card-content/card-content';
import { BotaoAcoes } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { FormularioValidatorUtil } from '@/shared/utils/formulario-validator-util';
import { DateUtils } from '@/shared/utils/date-utils';
import { SwitchSimNao } from '@/shared/components/switch-sim-nao/switch-sim-nao';
import { acaoMenuItem } from '@/shared/components/layout/botao-acoes/acao-menu-item';
import { AppMenuItem } from '@/core/permissions/permission.model';

export enum TipoRegistroEnum {
    PRINCIPAL = 'P',
    SECUNDARIA = 'S',
    OUTROS = 'O'
}

export enum AprovadoPorEnum {
    REUNIAO_DIRETORIA = 'D',
    SESSAO_PLENARIA = 'P',
    OUTROS = 'O'
}

@Component({
    selector: 'app-especialidade',
    standalone: true,
    imports: [SwitchSimNao, ReactiveFormsModule, Button, InputText, InputMask, Select, Textarea, SelectEstado, Tooltip, CardContent, BotaoAcoes, CanDirective],
    templateUrl: './especialidade.html',
    styleUrl: './especialidade.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class Especialidade implements OnInit, OnChanges {
    private readonly logger = inject(LoggerService);
    private readonly cdr = inject(ChangeDetectorRef);
    pessoa = input<Pessoa>();
    protected readonly roleEspecialidadeEditar = PERMISSOES.medicos.especialidade.editar;
    protected readonly roleEspecialidadeCriar = PERMISSOES.medicos.especialidade.criar;
    selectedDetail = input<EspecialidadeRequest>();
    modoCriacao = input<boolean>(false);
    voltar = output<void>();
    salvarCriacao = output<Partial<EspecialidadeRequest>>();
    salvarEdicao = output<EspecialidadeRequest>();
    cancelarCriacao = output<void>();
    remover = output<EspecialidadeRequest>();

    especialidadeForm!: FormGroup;
    modoEdicao = signal(false);
    ufCurso: string = 'BR';

    especialidadeCarregando = false;
    especialidadeErro?: string;

    aprovadoPorOptions = [
        { label: 'Reunião de Diretoria', value: AprovadoPorEnum.REUNIAO_DIRETORIA },
        { label: 'Sessão Plenária', value: AprovadoPorEnum.SESSAO_PLENARIA },
        { label: 'Outros', value: AprovadoPorEnum.OUTROS }
    ];

    tipoRegistroOptions = [
        { label: 'Principal', value: TipoRegistroEnum.PRINCIPAL },
        { label: 'Secundária', value: TipoRegistroEnum.SECUNDARIA },
        { label: 'Outros', value: TipoRegistroEnum.OUTROS }
    ];

    tipoDocumentoOptions: { label: string; value: number }[] = [];
    tipoDocumentoCarregando = false;
    tipoDocumentoErro?: string;
    private ultimoIdTipoDocumento?: number | null;
    get acoesFormulario(): AppMenuItem[] {
        const editando = this.modoEdicao();
        return [
            acaoMenuItem({
                id: 'editar',
                label: 'Editar',
                icon: 'pi pi-pencil',
                cor: 'editar',
                disabled: editando,
                permission: this.roleEspecialidadeEditar,
                command: () => this.habilitarEdicao()
            }),
            acaoMenuItem({
                id: 'remover',
                label: 'Remover',
                icon: 'pi pi-trash',
                cor: 'remover',
                disabled: editando,
                permission: PERMISSOES.medicos.especialidade.excluir,
                command: () => this.onRemoverSelecionado()
            })
        ];
    }

    /** Ver acoesFormulario: Salvar e Cancelar saem do menu e viram ação em destaque. */
    get itensEdicao(): AppMenuItem[] {
        const role = this.roleEspecialidadeEditar;
        return [
            // `?.`: o form só existe depois de inicializarFormulario(); o spec lê este getter
            // num componente criado sem detectChanges().
            acaoMenuItem({ id: 'salvar', label: 'Salvar', icon: 'pi pi-check', cor: 'salvar', disabled: !this.especialidadeForm?.valid, permission: role, command: () => this.salvar() }),
            acaoMenuItem({ id: 'cancelar', label: 'Cancelar', icon: 'pi pi-times', cor: 'cancelar', permission: role, command: () => this.cancelar() })
        ];
    }

    especialidadeOptions: { label: string; value: number }[] = [];
    areaAtuacaoOptions: { label: string; value: number }[] = [];
    especialidadeSelectCarregando = false;
    areaAtuacaoSelectCarregando = false;

    private especialidadesLista: EspecialidadeListaResponse[] = [];
    private areasAtuacaoLista: AreaAtuacaoPorEspecialidadeResponse[] = [];

    // Snapshot para restaurar valores ao cancelar edição
    private formSnapshotAntesEdicao: Record<string, unknown> | null = null;
    private ufCursoSnapshot: string | null = null;

    constructor(
        private fb: FormBuilder,
        private tiposDocService: EspecialidadeTiposDocumentoService,
        private especialidadeCoreService: EspecialidadeService
    ) {
        effect(() => {
            const divulgar = this.especialidadeForm?.get('divulgar');
            const perito = this.especialidadeForm?.get('perito');
            const modoEdicao = this.modoEdicao();
            if (divulgar) {
                modoEdicao ? divulgar.enable() : divulgar.disable();
            }

            if (perito) {
                modoEdicao ? perito.enable() : perito.disable();
            }
        });
    }

    ngOnInit(): void {
        this.inicializarFormulario();
        this.carregarTiposDocumentoEspecialidade();
        this.carregarEspecialidades();
        this.escutarMudancaEspecialidade();
        if (this.modoCriacao()) {
            this.modoEdicao.set(true);
        } else if (!this.modoEdicao()) {
            this.renderizarRegistroSelecionado();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedDetail'] && this.selectedDetail()) {
            this.modoEdicao.set(false);
            this.formSnapshotAntesEdicao = null;
            this.ufCursoSnapshot = null;
            this.areaAtuacaoOptions = [];
            this.renderizarRegistroSelecionado();
        }
    }

    private renderizarRegistroSelecionado() {
        const detail = this.selectedDetail();
        if (detail) {
            this.preencherFormularioEspecialidade(detail);
            if (detail.idEspecialidade && this.areaAtuacaoOptions.length === 0) {
                this.carregarAreasAtuacaoPorEspecialidade(detail.idEspecialidade, detail.idEspecArea);
            }
        }
    }

    inicializarFormulario(): void {
        this.especialidadeForm = this.fb.group({
            dtCertificado: ['', [Validators.required, this.dataMenorOuIgualHojeValidator()]],
            ufCurso: [this.ufCurso, Validators.required],
            especialidade: ['', Validators.required],
            divulgar: [{ value: true, disabled: true }],

            areaAtuacao: [''],
            resolucao: [''],

            numeroLivro: [''],
            numeroFolha: [''],
            dataRegistro: ['', [this.dataMenorOuIgualHojeValidator()]],
            cancelamento: ['', [this.dataMenorOuIgualHojeValidator()]],

            aprovadoPor: [''],
            aprovadoEm: ['', [this.dataMenorOuIgualHojeValidator()]],
            numeroAprovacao: [''],
            numeroRequerimento: [''],
            numeroRegistro: [''],

            tipoRegistro: [''],
            perito: [{ value: false, disabled: true }],

            tipoDocumento: [null],
            sobreDocumento: [''],
            observacoes: ['']
        });
    }

    private dataMenorOuIgualHojeValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const valor = String(control.value ?? '').trim();
            if (!valor) {
                return null;
            }

            if (!DateUtils.isDataValida(valor)) {
                return { dataInvalida: true };
            }

            return FormularioValidatorUtil.dataMaiorQueHoje(valor) ? { dataMaiorQueHojeInvalida: true } : null;
        };
    }

    private renderizarRegistroAtual(): void {
        const detail = this.selectedDetail();
        if (detail) this.preencherFormularioEspecialidade(detail);
    }

    private carregarTiposDocumentoEspecialidade(): void {
        this.tipoDocumentoCarregando = true;
        this.tipoDocumentoErro = undefined;
        this.tiposDocService
            .listarTiposDocumentoEspecialidade()
            .subscribe({
                next: (lista) => {
                    const ativos = (lista || []).filter((it) => it?.inStatus !== 'false');
                    this.tipoDocumentoOptions = ativos.map((it) => ({ label: it.dsTpDocEsp, value: it.id }));
                    this.tipoDocumentoCarregando = false;
                    this.aplicarTipoDocumentoInicial();
                },
                error: (err) => {
                    this.logger.error('Erro ao carregar tipos de documento de especialidade', err);
                    this.tipoDocumentoErro = 'Falha ao carregar tipos de documento.';
                    this.tipoDocumentoCarregando = false;
                }
            })
            .add(() => this.cdr.markForCheck());
    }

    private aplicarTipoDocumentoInicial(): void {
        if (!this.especialidadeForm) return;
        const ctrl = this.especialidadeForm.get('tipoDocumento');
        if (!ctrl) return;
        const current = ctrl.value;
        if (current !== null && current !== undefined && current !== '') return;
        if ((this.ultimoIdTipoDocumento ?? null) !== null && this.tipoDocumentoOptions?.length) {
            const id = this.ultimoIdTipoDocumento as number;
            const found = this.tipoDocumentoOptions.find((opt) => Number(opt.value) === Number(id));
            if (found) {
                ctrl.setValue(found.value, { emitEvent: false });
            }
        }
    }

    private preencherFormularioEspecialidade(e: EspecialidadeRequest): void {
        if (!this.especialidadeForm) return;

        this.ufCurso = this.normalizarUfCurso(e.ufCurso);

        // Verifica se o valor de inPrincipal é válido conforme o Enum
        const inPrincipalRaw = (e.inPrincipal ?? '').toUpperCase();
        const tipoRegistroValido = Object.values(TipoRegistroEnum).includes(inPrincipalRaw as TipoRegistroEnum) ? (inPrincipalRaw as TipoRegistroEnum) : TipoRegistroEnum.OUTROS;

        this.especialidadeForm.patchValue(
            {
                dtCertificado: this.formatarData(e.dtCertificado),
                ufCurso: this.ufCurso,
                especialidade: e.idEspecialidade ?? '',
                divulgar: e.inDivulga === 'S',

                areaAtuacao: e.idEspecArea ?? '',
                resolucao: e.resolucao ?? '',

                numeroLivro: e.nuLivroRegistro ?? '',
                numeroFolha: e.nuFolhaRegistro ?? '',
                dataRegistro: this.formatarData(e.dtEmissao),
                cancelamento: this.formatarData(e.dtCancelamento),

                aprovadoPor: (e as { inReuniao?: string }).inReuniao ?? '',

                aprovadoEm: this.formatarData(e.dtReuniao),
                numeroAprovacao: e.nuReuniao ?? '',
                numeroRequerimento: e.nuRequerimentoEspecialidade ?? '',
                numeroRegistro: e.nuRegistro ?? '',

                // Uso do Enum e lógica tratada acima
                tipoRegistro: tipoRegistroValido,

                perito: e.inPerito === 'S',

                tipoDocumento: e.idTpDocumento ?? null,
                sobreDocumento: e.txObsDocumento ?? '',
                observacoes: e.txObservacao ?? ''
            },
            { emitEvent: false }
        );

        this.ultimoIdTipoDocumento = e.idTpDocumento ?? null;
        this.aplicarTipoDocumentoInicial();
    }

    private normalizarUfCurso(ufCurso?: string | null): string {
        const uf = (ufCurso ?? '').trim().toUpperCase();
        return uf === 'BR' ? '' : uf;
    }

    private formatarData(iso?: string): string {
        if (!iso) return '';
        const datePart = iso.split('T')[0];
        const parts = datePart.split('-');
        if (parts.length === 3) {
            const [yyyy, mm, dd] = parts;
            const d = dd.padStart(2, '0');
            const m = mm.padStart(2, '0');
            return `${d}/${m}/${yyyy}`;
        }
        const dObj = new Date(iso);
        if (isNaN(dObj.getTime())) return '';
        const d = String(dObj.getDate()).padStart(2, '0');
        const m = String(dObj.getMonth() + 1).padStart(2, '0');
        const y = dObj.getFullYear();
        return `${d}/${m}/${y}`;
    }

    habilitarEdicao(): void {
        if (this.especialidadeForm) {
            this.formSnapshotAntesEdicao = this.especialidadeForm.getRawValue();
            this.ufCursoSnapshot = this.ufCurso;
        }
        this.modoEdicao.set(true);
    }

    private mapearFormParaRequest(): Partial<EspecialidadeRequest> {
        const v = this.especialidadeForm.getRawValue();

        const areaSelecionada = this.areasAtuacaoLista.find((a) => a.idEspecArea === v.areaAtuacao);
        const especialidadeSelecionada = this.especialidadesLista.find((e) => e.idEspecialidade === v.especialidade);

        const idEspecArea = areaSelecionada?.idEspecArea ?? 0;

        return {
            idEspecialidade: especialidadeSelecionada?.idEspecialidade ?? v.especialidade ?? 0,
            dsEspecialidade: especialidadeSelecionada?.dsEspecialidade ?? '',
            idEspecArea,
            dsAreaAtuacao: areaSelecionada?.dsAreaAtuacao ?? '',
            idAreaAtuacao: areaSelecionada?.idAreaAtuacao ?? 0,
            especialidIdEspecialidade: idEspecArea,
            inDivulga: v.divulgar ? 'S' : 'N',
            dtCertificado: v.dtCertificado,
            nuLivroRegistro: v.numeroLivro ?? '',
            nuFolhaRegistro: v.numeroFolha ?? '',
            dtEmissao: v.dataRegistro,
            dtCancelamento: v.cancelamento,
            inReuniao: v.aprovadoPor ?? '',
            dtReuniao: v.aprovadoEm,
            inPrincipal: v.tipoRegistro ?? '',
            nuReuniao: v.numeroAprovacao ?? '',
            nuRequerimentoEspecialidade: v.numeroRequerimento ?? '',
            nuRegistro: v.numeroRegistro ?? '',
            inPerito: v.perito ? 'S' : 'N',
            idTpDocumento: v.tipoDocumento ?? 0,
            txObsDocumento: v.sobreDocumento ?? '',
            txObservacao: v.observacoes ?? '',
            resolucao: v.resolucao ?? '',
            ufCurso: v.ufCurso ?? this.ufCurso ?? ''
        };
    }

    private mapearFormParaEdicao(): EspecialidadeRequest {
        const detail = this.selectedDetail();
        const v = this.especialidadeForm.getRawValue();

        const areaSelecionada = this.areasAtuacaoLista.find((a) => a.idEspecArea === v.areaAtuacao);
        const especialidadeSelecionada = this.especialidadesLista.find((e) => e.idEspecialidade === v.especialidade);
        const idEspecArea = areaSelecionada?.idEspecArea ?? (detail as EspecialidadeRequest).idEspecArea;

        return {
            ...(detail as EspecialidadeRequest),
            idEspecialidade: especialidadeSelecionada?.idEspecialidade ?? (detail as EspecialidadeRequest).idEspecialidade,
            dsEspecialidade: especialidadeSelecionada?.dsEspecialidade ?? (detail as EspecialidadeRequest).dsEspecialidade,
            idEspecArea,
            dsAreaAtuacao: areaSelecionada?.dsAreaAtuacao ?? (detail as EspecialidadeRequest).dsAreaAtuacao,
            idAreaAtuacao: areaSelecionada?.idAreaAtuacao ?? (detail as EspecialidadeRequest).idAreaAtuacao,
            especialidIdEspecialidade: idEspecArea,
            inDivulga: v.divulgar ? 'S' : 'N',
            dtCertificado: v.dtCertificado,
            nuLivroRegistro: v.numeroLivro ?? '',
            nuFolhaRegistro: v.numeroFolha ?? '',
            dtEmissao: v.dataRegistro,
            dtCancelamento: v.cancelamento,
            inReuniao: v.aprovadoPor ?? '',
            dtReuniao: v.aprovadoEm,
            inPrincipal: v.tipoRegistro ?? '',
            nuReuniao: v.numeroAprovacao ?? '',
            nuRequerimentoEspecialidade: v.numeroRequerimento ?? '',
            nuRegistro: v.numeroRegistro ?? '',
            inPerito: v.perito ? 'S' : 'N',
            idTpDocumento: v.tipoDocumento ?? 0,
            txObsDocumento: v.sobreDocumento ?? '',
            txObservacao: v.observacoes ?? '',
            resolucao: v.resolucao ?? '',
            ufCurso: v.ufCurso ?? this.ufCurso ?? ''
        };
    }

    private carregarEspecialidades(): void {
        this.especialidadeSelectCarregando = true;
        this.especialidadeCoreService
            .listar()
            .subscribe({
                next: (especialidades) => {
                    this.especialidadesLista = especialidades || [];
                    this.especialidadeOptions = this.especialidadesLista.map((e) => ({
                        label: e.dsEspecialidade ?? '',
                        value: e.idEspecialidade ?? 0
                    }));
                    this.especialidadeSelectCarregando = false;
                },
                error: (err) => {
                    this.logger.error('Erro ao carregar especialidades', err);
                    this.especialidadeSelectCarregando = false;
                }
            })
            .add(() => this.cdr.markForCheck());
    }

    private carregarAreasAtuacaoPorEspecialidade(idEspecialidade: number, valorAreaInicial?: number): void {
        this.areaAtuacaoSelectCarregando = true;
        this.especialidadeCoreService
            .listarAreasAtuacaoPorEspecialidade(idEspecialidade)
            .subscribe({
                next: (areas) => {
                    this.areasAtuacaoLista = areas || [];
                    this.areaAtuacaoOptions = this.areasAtuacaoLista.map((a) => ({
                        label: a.dsAreaAtuacao ?? '',
                        value: a.idEspecArea ?? 0
                    }));
                    this.areaAtuacaoSelectCarregando = false;
                    if (valorAreaInicial !== undefined) {
                        this.especialidadeForm.get('areaAtuacao')?.setValue(valorAreaInicial, { emitEvent: false });
                    }
                },
                error: (err) => {
                    this.logger.error('Erro ao carregar áreas de atuação', err);
                    this.areaAtuacaoSelectCarregando = false;
                }
            })
            .add(() => this.cdr.markForCheck());
    }

    private escutarMudancaEspecialidade(): void {
        this.especialidadeForm.get('especialidade')?.valueChanges.subscribe((idEspecialidade: number) => {
            this.especialidadeForm.get('areaAtuacao')?.setValue(null, { emitEvent: false });
            this.areaAtuacaoOptions = [];
            if (!idEspecialidade) {
                return;
            }
            this.carregarAreasAtuacaoPorEspecialidade(idEspecialidade);
        });
    }

    salvar(): void {
        if (this.especialidadeForm.valid) {
            if (this.modoCriacao()) {
                this.salvarCriacao.emit(this.mapearFormParaRequest());
                return;
            }
            this.salvarEdicao.emit(this.mapearFormParaEdicao());
        } else {
            this.especialidadeForm.markAllAsTouched();
            this.especialidadeForm.markAllAsDirty();
        }
    }

    cancelar(): void {
        if (this.modoCriacao()) {
            this.cancelarCriacao.emit();
            return;
        }
        if (this.formSnapshotAntesEdicao) {
            this.especialidadeForm.reset(this.formSnapshotAntesEdicao);
            if (this.ufCursoSnapshot !== null) {
                this.ufCurso = this.ufCursoSnapshot;
            }
        } else {
            this.renderizarRegistroAtual();
        }
        this.modoEdicao.set(false);
        this.formSnapshotAntesEdicao = null;
        this.ufCursoSnapshot = null;
    }

    onRemover(especialidade: EspecialidadeRequest) {
        this.remover.emit(especialidade);
    }

    private onRemoverSelecionado(): void {
        const especialidadeSelecionada = this.selectedDetail();
        if (!especialidadeSelecionada) return;
        this.onRemover(especialidadeSelecionada);
    }

    campoInvalido(nomeCampo: string): boolean {
        const campo = this.especialidadeForm?.get(nomeCampo);
        return !!campo && campo.invalid && (campo.touched || campo.dirty);
    }

    mensagemErroCampo(nomeCampo: string): string | null {
        const campo = this.especialidadeForm?.get(nomeCampo);
        if (!campo?.errors) return null;

        if (campo.errors['required']) return 'Campo obrigatório.';
        if (campo.errors['dataMaiorQueHojeInvalida']) return 'A data deve ser menor ou igual a hoje.';
        return 'Valor inválido.';
    }
}
